@let flightIsActive = flightIsActive$ | async;
@let bundleNotAvailableForFlightForPax = bundleNotAvailableForFlightForPax$ | async;
@let seatMapWrapper = seatMapElement()?.seatMapWrapper;
@let overlaps = serviceOverlapsForActivePax();

<ng-template #loader>
  <fcom-loader [loaderType]="LoaderTheme.EMBEDDED" />
</ng-template>

<ng-template #operatingAirlineNotification let-operatingAirlineIdInfo="operatingAirlineIdInfo">
  <fcom-notification
    data-testid="operating-airline-notification"
    class="ms-small-t"
    [title]="
      (operatingAirlineIdInfo.isOrder
        ? 'ancillaries.seat.operatingAirlineNotification.header.mmb'
        : 'ancillaries.seat.operatingAirlineNotification.header.booking'
      ) | finLocalization
    "
  >
    <span data-testid="operating-airline-notification-message" class="font-body-1 ps-small-b wrap font-body-medium">{{
      (operatingAirlineIdInfo.isOrder
        ? 'ancillaries.seat.operatingAirlineNotification.generalMessage.mmb'
        : 'ancillaries.seat.operatingAirlineNotification.generalMessage.booking'
      ) | finLocalization
    }}</span>
    <span
      *ngIf="operatingAirlineIdInfo.isOrder"
      data-testid="operating-airline-notification-pnr-message"
      class="font-body-1 ps-small-b font-body-medium"
    >
      {{
        (operatingAirlineIdInfo.isOrderIdIdentical
          ? 'ancillaries.seat.operatingAirlineNotification.pnrMessage.sameId'
          : 'ancillaries.seat.operatingAirlineNotification.pnrMessage.differentId'
        ) | finLocalization
      }}
      <span class="bold-type">
        {{ operatingAirlineIdInfo.operatingAirlineId.id }}
      </span>
    </span>
  </fcom-notification>
</ng-template>

<ng-template #fallbackNotification>
  <h4 class="ps-medium-b align-center">
    {{ 'services.cannotAdd' | finLocalization }}
  </h4>
</ng-template>

<ng-template #nonFinnairNoAvailability let-notification="notification">
  <div
    [attr.aria-label]="'bookingSummary.operatedBy' | finLocalization"
    class="ms-medium-b airline-tail {{ currentFlightTail }}"
  ></div>
  <h4 class="ps-medium-b align-center" *ngIf="notification.key | finHasLocalizationKey; else fallbackNotification">
    {{ notification.key | finLocalization }}
  </h4>
  <p *ngIf="notification.reason">
    {{ notification.reason.key | finLocalization: notification.reason.data }}
  </p>
</ng-template>

<button id="seatMapStart" (click)="moveFocus($event, 'seatMapEnd')" class="is-vishidden">
  {{ 'ancillaries.seats.skipSeatSelection' | finLocalization }}
</button>

<div
  class="inline-block valign-top fill"
  [attr.data-testid]="flightIsActive ? 'active-seat-map' : undefined"
  [attr.data-flight-id]="flight().id"
  [class.rounded-large]="isNewBrand()"
  [class.surface-primary]="isNewBrand()"
  [class.ps-medium]="isNewBrand()"
  [class.border]="isNewBrand()"
>
  <ng-container *ngIf="notEligibleWhenCheckInClosed">
    <fcom-notification
      data-testid="seat-map-not-available-after-check-in-notification"
      class="block ps-small-y ps-small-t"
      [theme]="NotificationTheme.INFO"
    >
      {{ 'services.seat.notAvailableWhenCheckInClosed' | finLocalization }}
    </fcom-notification>
  </ng-container>
  <ng-container *ngIf="!notEligibleWhenCheckInClosed">
    <ng-container
      *ngIf="
        ((seatMapStatus$ | async) === SeatMapStatus.OK && (selectablePassengers$ | async)?.length) ||
          (seatMapStatus$ | async) === SeatMapStatus.OK_NO_AVAILABILITY ||
          (seatMapStatus$ | async) === SeatMapStatus.LOAD_ERROR;
        else loader
      "
    >
      <div *ngIf="(seatMapStatus$ | async) === SeatMapStatus.LOAD_ERROR" class="error-seatmap ps-medium align-center">
        <fcom-icon
          [attr.aria-label]="'ancillaries.seats.alert' | finLocalization"
          class="ms-xsmall-l ps-small-b icon-xlarge"
          [name]="SvgLibraryIcon.ALERT_TRIANGLE"
        />
        <h4 class="ps-medium-y">
          {{ 'ancillaries.seats.loadErrorTitle' | finLocalization }}
        </h4>
        <p>{{ 'ancillaries.seats.loadErrorContent' | finLocalization }}</p>
        <fcom-button
          class="retry-button ps-large-t"
          [text]="'ancillaries.seats.retry' | finLocalization"
          (clickBtn)="retrySeatmap.emit(flight())"
        />
      </div>
      <ng-container *ngIf="(seatMapStatus$ | async) === SeatMapStatus.OK_NO_AVAILABILITY">
        <div class="unavailable-seatmap ps-medium-y flex flex--column flex--center" data-testid="unavailable-seatmap">
          <ng-container *ngIf="noSeatmapNotifications$ | async as notifications">
            <ng-container *ngFor="let notification of notifications">
              @if (notification?.reason?.data?.airline) {
                <ng-container *ngTemplateOutlet="nonFinnairNoAvailability; context: { notification: notification }" />
              } @else {
                <fcom-notification
                  data-testid="seatmap-not-available-notification-reason"
                  class="ps-medium-b"
                  [iconCategory]="IconLibrary.SVG_LIBRARY"
                  [iconName]="notification.iconName"
                  [theme]="notification.theme"
                >
                  <span [innerHTML]="notification.key | finLocalization"></span>
                </fcom-notification>
              }
            </ng-container>
          </ng-container>
        </div>
        @if (
          (operatingAirlineIdInfo$ | async)?.isOperated &&
          !(operatingAirlineIdInfo$ | async)?.travelExtrasPurchaseNotEligible
        ) {
          <ng-container
            *ngTemplateOutlet="
              operatingAirlineNotification;
              context: { operatingAirlineIdInfo: operatingAirlineIdInfo$ | async }
            "
          />
        }
      </ng-container>
      <ng-container *ngIf="(seatMapStatus$ | async) === SeatMapStatus.OK">
        <fcom-notification
          *ngIf="unmodifiableSeat$ | async"
          [iconName]="SvgLibraryIcon.CHECKMARK"
          class="notification pr-small-b"
        >
          {{ 'MMB.view.seatMap.paidSeatNotification' | finLocalization }}
        </fcom-notification>
        <fcom-notification
          *ngIf="unmodifiableFreeSeat$ | async"
          [iconName]="SvgLibraryIcon.CHECKMARK"
          class="notification pr-small-b"
        >
          {{ 'MMB.view.seatMap.ticketedSeatNotification' | finLocalization }}
        </fcom-notification>
        @if (bundleNotAvailableForFlightForPax) {
          <fcom-notification class="block ms-small-b" data-testid="bundle-unavailable-notification">
            {{ 'ancillaries.bundle.unavailableBundle' | finLocalization }}
          </fcom-notification>
        }

        <fin-seat-passengers
          [passengers$]="selectablePassengers$"
          [flight]="flight()"
          [selectedSeatsByPax$]="selectedSeatsByPax$"
          [selectedBundleCategoriesByPax$]="selectedBundleCategoriesByPax$"
          (selectPax)="selectPassenger($event)"
          [activePaxId]="activePaxId()"
        />

        <div class="ps-medium-b" aria-live="polite" aria-atomic="true" data-testid="overlap-notification-live-region">
          @if (overlaps.showNotification && !bundleNotAvailableForFlightForPax) {
            <fin-service-overlap-notification
              [category]="contextCategory()"
              [overlappingServices]="overlaps.services"
              (continueSelection)="dismissOverlapNotificationForPax(activePaxId(), $event)"
              (cancelSelection)="keepExistingSelectionForPax(activePaxId())"
            />
          }
        </div>

        @if (bundleModifyingEnabled$ | async) {
          @if (selectionBlockedByQuota$ | async) {
            <fcom-notification
              data-testid="bundle-quota-reached-notification"
              class="block ms-small-b"
              [theme]="NotificationTheme.WARNING"
            >
              {{ 'ancillaries.bundle.quotaReached' | finLocalization }}
            </fcom-notification>
          }
          @if (displayBundlePromotionCard()) {
            <fin-bundle-promotion-card
              [category]="contextCategory()"
              [flightId]="flight().id"
              [passengerId]="activePaxId()"
              [purchaseFlow]="purchaseFlow"
              [forceNotInteractive]="overlaps.blockSelections"
              (toggled)="onBundleToggle()"
            />
          }

          @if (incompatibleComboSeat$ | async; as incompatibleSeat) {
            <fcom-notification
              class="block ms-small-b"
              data-testid="incompatible-combo-seat-notification"
              [theme]="NotificationTheme.WARNING"
            >
              {{
                'ancillaries.bundle.seatMapToggle.incompatibleSeat'
                  | finLocalization
                    : {
                        seatType: incompatibleSeat.seatTypeKey | finLocalization,
                        seatNumber: incompatibleSeat.seatNumber,
                      }
              }}
            </fcom-notification>
          }
        }

        @if (benefitTextKey) {
          <fin-seat-benefit [descriptionKey]="benefitTextKey" [tag]="benefitTag" />
        }

        @if (seatSelectionDisabledForBundle$ | async) {
          <p class="is-vishidden" role="status">{{ 'ancillaries.seats.selectionDisabled' | finLocalization }}</p>
        }

        <div
          data-testid="seat-map-selection-wrapper"
          [attr.inert]="(seatSelectionDisabledForBundle$ | async) ? true : null"
          [class.seat-map-disabled]="seatSelectionDisabledForBundle$ | async"
        >
          @if (seatMapWrapper) {
            <fin-seat-map-seatchips
              [flight]="flight()"
              [contextCategory]="contextCategory()"
              [activePaxId$]="activePaxId$"
              [displayBundles$]="displayBundleChips$"
              [includeBabyBassinetSeats]="passengersIncludeInfants()"
              [selectedSeats$]="selectedSeats$"
              [seatMapElement]="seatMapWrapper"
              (scrollToSeat)="scrollToSeat($event)"
            />
          }

          <fin-seat-map-renderer
            #seatMap
            [flight]="flight()"
            [seatMapContainerWidth$]="seatMapContainerWidth$"
            [passengers$]="selectablePassengers$"
            [activePaxId$]="activePaxId$"
            [selectedSeatsByPax$]="selectedSeatsByPax$"
            [selectedBundleCategoriesByPax$]="selectedBundleCategoriesByPax$"
            [enabledBundleCategory$]="displayBundleChips$"
            [seatSelectionDisabled$]="seatSelectionDisabledForBundle$"
            [unavailableSeatLabel]="'seatMap.seats.unavailable' | finLocalization"
            [includedSeats]="includedSeats"
            (selectSeat)="selectSeat($event)"
            (updateScrollPosition)="($event)"
          />

          <div class="flex flex--column flex--center mr-xsmall-y" data-testid="aircraft-model">
            <h5>{{ 'seatMap.airCraftModel' | finLocalization }}</h5>
            <span class="font-body-1 font-body-medium" *ngIf="isFinnairWetLease">
              {{ flight().aircraft.name }}
            </span>
            <span
              *ngIf="!isFinnairWetLease"
              class="font-body-1 flex flex--center ps-xxsmall font-body-medium"
              id="aircraft-read-more"
              >{{ 'aircrafts.' + flight().aircraft.code | finLocalization }} ({{ flight().aircraft.code }})
              <fcom-icon-button
                data-testid="aircraft-read-more-button"
                class="flex flex--self-center ps-xxsmall ms-xxsmall-l"
                [icon]="{ name: SvgLibraryIcon.INFO }"
                [theme]="IconButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE"
                [size]="IconButtonSize.SMALL"
                [aria]="{ label: 'readMore' | finLocalization }"
                aria-labelledby="aircraft-read-more"
                (clickBtn)="openAirplaneModal()"
              />
            </span>
          </div>
        </div>
      </ng-container>

      <fin-multi-city-survey [extraClasses]="'block mr-small-b'" />
    </ng-container>
  </ng-container>
</div>

<button id="seatMapEnd" (click)="moveFocus($event, 'seatMapStart')" class="is-vishidden">
  {{ 'ancillaries.seats.backToSeatMap' | finLocalization }}
</button>

<fcom-modal
  [(open)]="exitRowModalOpen"
  [title]="'ancillaries.seats.popover.confirmExitRowTitle' | finLocalization"
  [buttons]="ModalButtons.CUSTOM"
>
  <div class="pr-large-x pr-medium-y block wrap">
    <fin-seat-details [seatDetails$]="seatDetails$" [hideButtons]="true" />
  </div>
  <div buttons *ngIf="seatDetails$ | async as seatDetails">
    <fcom-button
      class="mr-xsmall-r"
      data-testid="cancel-selection-button"
      [text]="'actions.cancel' | finLocalization"
      [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
      [aria]="{ label: 'actions.cancel' | finLocalization, role: 'button' }"
      (clickBtn)="exitRowModalOpen = false"
    />
    <fcom-button
      data-testid="confirm-selection-button"
      [text]="'ancillaries.seats.popover.confirmExitRowAction' | finLocalization"
      [aria]="{ label: 'ancillaries.seats.popover.confirmExitRowAction' | finLocalization, role: 'button' }"
      [theme]="ButtonTheme.PRIMARY_ACCENT"
      (clickBtn)="confirmExitRowSeat(seatDetails.travelerId, seatDetails.seatNumber)"
    />
  </div>
</fcom-modal>

@if (aircraftInformationTemplate$ | async) {
  <fcom-modal [(open)]="aircraftModalOpen" [title]="flight().aircraft.name">
    <div class="wrap pr-large-x">
      <fin-cms-json-content [template$]="aircraftInformationTemplate$" />
    </div>
  </fcom-modal>
}

<fcom-modal
  data-testid="seat-removal-confirm-modal"
  [open]="bundleSeatRemovalConfirmModalOpen"
  [title]="'services.seat.bundleUnavailableSeatConfirmModal.title' | finLocalization"
  [buttons]="ModalButtons.CUSTOM"
  [fitToContent]="true"
  (close)="closeSeatRemovalBundleModal()"
  [id]="'confirmationModal'"
>
  <div class="pr-large-x pr-medium-y block wrap">
    <p
      [innerHTML]="
        'services.seat.bundleUnavailableSeatConfirmModal.firstContentParagraph' | finLocalization | finSafe: 'html'
      "
    ></p>

    <p
      class="mr-xsmall-t"
      [innerHTML]="
        'services.seat.bundleUnavailableSeatConfirmModal.secondContentParagraph' | finLocalization | finSafe: 'html'
      "
    ></p>
  </div>
  <div buttons>
    <fcom-button
      [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
      (clickBtn)="closeSeatRemovalBundleModal()"
      [text]="'services.seat.bundleUnavailableSeatConfirmModal.closeButton' | finLocalization"
      [aria]="{ label: 'actions.cancel' | finLocalization, role: 'button' }"
      class="mr-xsmall-r"
      data-testid="seat-removal-confirm-modal-close"
    />
    <fcom-button
      (clickBtn)="confirmSeatRemovalAndBundleActivation()"
      [text]="'services.seat.bundleUnavailableSeatConfirmModal.continueButton' | finLocalization"
      [aria]="{ label: 'actions.continue' | finLocalization, role: 'button' }"
      data-testid="seat-removal-confirm-modal-continue"
    />
  </div>
</fcom-modal>
